/**
 * Description d'un élément du DOM pour un ticket : de quoi le retrouver dans la page et
 * dans le code, sans dépendre des outils de développement d'Angular, absents en production.
 */

/** Profondeur maximale du sélecteur : au-delà, il devient illisible sans gagner en précision. */
const MAX_SELECTOR_DEPTH = 5;
/** Classes conservées par segment : les suivantes relèvent souvent de l'état (active, hover…). */
const MAX_CLASSES_PER_SEGMENT = 2;

/**
 * Classes générées par le framework ou le navigateur : elles changent d'un build à l'autre
 * et ne disent rien au lecteur du ticket.
 */
const NOISE_CLASS = /^(ng-|cdk-|mat-mdc-|_ng)/;

/**
 * Composants Angular englobant l'élément, de la racine vers lui : « app-root > app-blast > … ».
 * Un sélecteur de composant contient toujours un tiret, ce qui suffit à le distinguer des
 * balises natives.
 */
export function componentPath(element: Element): string {
  const components: string[] = [];
  for (let node: Element | null = element; node; node = node.parentElement) {
    const tag = node.tagName.toLowerCase();
    if (tag.includes('-')) {
      components.unshift(tag);
    }
  }
  return components.join(' > ');
}

/** Composant Angular le plus proche de l'élément, lui compris. */
function closestComponent(element: Element): string | null {
  for (let node: Element | null = element; node; node = node.parentElement) {
    const tag = node.tagName.toLowerCase();
    if (tag.includes('-')) {
      return tag;
    }
  }
  return null;
}

/**
 * Sélecteur CSS court menant à l'élément. Il s'arrête au premier ancêtre portant un id, qui
 * l'ancre suffisamment, ou après quelques niveaux.
 */
export function cssSelector(element: Element): string {
  const segments: string[] = [];

  for (let node: Element | null = element; node && segments.length < MAX_SELECTOR_DEPTH; node = node.parentElement) {
    if (node.id && isStableId(node.id)) {
      segments.unshift(`#${CSS.escape(node.id)}`);
      break;
    }
    segments.unshift(segmentOf(node));
    if (node.tagName === 'BODY') {
      break;
    }
  }

  return segments.join(' > ');
}

/** Libellé affiché au survol, à la manière de l'inspecteur : « div.card · app-blast-kpi ». */
export function hoverLabel(element: Element): string {
  const own = shortSegment(element);
  const component = closestComponent(element);
  return component && component !== element.tagName.toLowerCase() ? `${own} · ${component}` : own;
}

function segmentOf(node: Element): string {
  let segment = shortSegment(node, true);

  const parent = node.parentElement;
  if (parent) {
    const sameTag = Array.from(parent.children).filter(child => child.tagName === node.tagName);
    if (sameTag.length > 1) {
      segment += `:nth-of-type(${sameTag.indexOf(node) + 1})`;
    }
  }
  return segment;
}

function shortSegment(node: Element, escape = false): string {
  const tag = node.tagName.toLowerCase();
  const classes = Array.from(node.classList)
    .filter(name => !NOISE_CLASS.test(name))
    .slice(0, MAX_CLASSES_PER_SEGMENT)
    .map(name => `.${escape ? CSS.escape(name) : name}`);
  return tag + classes.join('');
}

/** Un id généré (mat-input-3, cdk-overlay-0, uuid…) change à chaque rendu : il ne sert pas d'ancre. */
function isStableId(id: string): boolean {
  return !/\d{2,}|^(mat|cdk|ng)-|[0-9a-f]{8}-/.test(id);
}
